import {UserDefinedObjectTemplateAdditionalColumnSourceType as UdoColumnSource} from "@vesta/vos-types/modules/workspaceLevel/userDefinedObjects/domain/UserDefinedObjectTemplateAdditionalColumn";
import {useUserDefinedObjectFieldValidation} from "~/components/userDefinedObjectAdditionalColumnField";
import type {UserDefinedObjectTemplateAdditionalColumn as UdoColumn} from "@vesta/vos-types/modules/workspaceLevel/userDefinedObjects/domain/UserDefinedObjectTemplateAdditionalColumn";
import {getUserDefinedObjectAdditionalColumnDetails} from "~/domainModels/userDefinedObjectAdditionalColumns";
import {UserDefinedObjectTemplateAdditionalColumnType as UdoColumnType} from "@vesta/vos-types/modules/workspaceLevel/userDefinedObjects/domain/UserDefinedObjectTemplateAdditionalColumn";
import {useCapabilityUdoColumns} from "~/contexts/workspaceContext";
import {useMutation, useQueryClient} from "@tanstack/react-query";
import {invalidateOttopilotDataViewerQueries} from "~/components/ottopilotDataViewer/invalidateOttopilotDataViewerQueries";
import type {Dispatch, ReactElement, SetStateAction} from "react";
import {useEffect, useMemo, useState} from "react";
import {GenericModal} from "~/components/genericModal";
import {useUnsavedChangesGuard} from "~/hooks/useUnsavedChangesGuard";
import {isFormValueDirty} from "~/utilities/isFormValueDirty";
import {useOttopilotConfiguration} from "~/contexts/ottopilotConfigurationContext";
import {useWorkspace} from "~/contexts/workspaceContext";
import {useWorkspaceUser} from "~/contexts/workspaceUserContext";
import type {OttopilotEventTemplate} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventsWorkspaceCapability/domain/OttopilotEventTemplate";
import {OttopilotAdditionalButtonKind, OttopilotAdditionalButtonFormFieldPresentation, OttopilotRunTimeValueKind, type OttopilotAdditionalButtonFormColumnData, type OttopilotAdditionalButtonsConfig} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventsWorkspaceCapability/domain/OttopilotEventAdditionalButtonsConfig";
import {getAdditionalButtonCancelRemarksColumn, getAdditionalButtonFormColumnsSorted} from "~/domainModels/eventTemplates";

import type {EventContainerTemplate as JourneysModuleConfiguration} from "~/domainModels/workspaceModules/eventContainersModule";
import {AnankeButton} from "@vesta/ananke/components/anankeButton";
import {AnankeField} from "@vesta/ananke/components/anankeField";
import {AnankeTextArea} from "@vesta/ananke/components/anankeTextArea";
import {AnankeTextField} from "@vesta/ananke/components/anankeTextField";
import {useAnankeFlags} from "@vesta/ananke/hooks/useAnankeFlags";
import type {Iso8601DateTime, Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {getCurrentIsoTimestamp} from "@vesta/global-common-typescript/utilities/utilities";
import {isAmaronWorkspace} from "~/domainModels/workspaceSpecific/amaron";
import {DuluxEventTypes, isDuluxWorkspace} from "~/domainModels/workspaceSpecific/dulux";
import {amaronCancelCallAction} from "~/utilities/apis/amaronActionsApi";
import {duluxCancelAppointmentAction, duluxCancelCallAction} from "~/utilities/apis/duluxActionsApi";
import {updateEvent, type EventWithEventContainer} from "~/utilities/apis/api";
import {getAdditionalColumnOptions} from "~/domainModels/additionalColumns";

type LooseAdditionalColumnEditParams = {
    value: unknown;
    setValue: Dispatch<SetStateAction<unknown>>;
    options?: ReadonlyArray<{id: Uuid; displayName: string}>;
};

type LooseAdditionalColumnEditComponent = (params: LooseAdditionalColumnEditParams) => ReactElement;

function unknownToSingleLineFieldString(value: unknown): string {
    if (value === undefined || value === null) {
        return "";
    }
    if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
        return String(value);
    }
    return "";
}

function isNonBlankInitialColumnValue(value: unknown): boolean {
    if (value === undefined || value === null) {
        return false;
    }
    if (typeof value === "string") {
        return value.trim() !== "";
    }
    if (typeof value === "number" || typeof value === "boolean") {
        return true;
    }
    if (Array.isArray(value)) {
        return value.length > 0;
    }
    return true;
}

function isEmptyForRequiredField(value: unknown): boolean {
    if (value === undefined || value === null) {
        return true;
    }
    if (typeof value === "string") {
        return value.trim() === "";
    }
    if (typeof value === "number" || typeof value === "boolean") {
        return false;
    }
    if (Array.isArray(value)) {
        return value.length === 0;
    }
    return false;
}

function getExistingColumnValue(column: OttopilotAdditionalButtonFormColumnData, event: EventWithEventContainer): unknown {
    if (column.columnType === "OttopilotEvent") {
        return event.additionalColumnValues[column.columnId];
    }
    return event.eventContainer.additionalColumnValues[column.columnId];
}

function buildInitialVisibleFieldValue(params: {column: OttopilotAdditionalButtonFormColumnData; event: EventWithEventContainer}): unknown {
    const existing = getExistingColumnValue(params.column, params.event);
    if (isNonBlankInitialColumnValue(existing) === true) {
        return existing;
    }
    const column = params.column;
    if (column.kind === OttopilotRunTimeValueKind.static) {
        return column.defaultValue ?? "";
    }
    if (column.kind === OttopilotRunTimeValueKind.user || column.kind === OttopilotRunTimeValueKind.userTemplate) {
        return "";
    }
    if (column.kind === OttopilotRunTimeValueKind.timestamp) {
        return "";
    }
    return column.defaultValue ?? 0;
}

function buildSubmitPayload(params: {
    sortedColumns: Array<OttopilotAdditionalButtonFormColumnData>;
    formValues: Record<string, unknown>;
    heimdallUserId: Uuid;
    workspaceUserTemplateId: Uuid;
}): Record<Uuid, unknown> {
    const payload: Record<Uuid, unknown> = {};
    for (const col of params.sortedColumns) {
        if (col.columnType !== "OttopilotEvent") {
            if (process.env.NODE_ENV === "development") {
                console.warn("[EventAdditionalButtonFormModal] Journey column omitted from submit (v1 Event-only)", col.columnId);
            }
            continue;
        }
        const isHiddenOrReadOnly = col.isVisible === false || col.readOnly === true;
        if (isHiddenOrReadOnly === true) {
            if (col.kind === OttopilotRunTimeValueKind.timestamp) {
                payload[col.columnId] = getCurrentIsoTimestamp();
            } else if (col.kind === OttopilotRunTimeValueKind.user) {
                payload[col.columnId] = params.heimdallUserId;
            } else if (col.kind === OttopilotRunTimeValueKind.userTemplate) {
                payload[col.columnId] = params.workspaceUserTemplateId;
            } else if (col.kind === OttopilotRunTimeValueKind.static) {
                payload[col.columnId] = col.defaultValue;
            } else {
                payload[col.columnId] = col.defaultValue ?? 0;
            }
        } else {
            payload[col.columnId] = params.formValues[col.columnId];
        }
    }
    return payload;
}

function getAdditionalButtonFormFieldDisplayLabel(params: {
    column: OttopilotAdditionalButtonFormColumnData;
    eventColumnDisplayNameById: ReadonlyMap<Uuid, string>;
    journeyColumnDisplayNameById: ReadonlyMap<Uuid, string>;
}): string {
    const explicit = params.column.label;
    if (explicit !== undefined && explicit.trim() !== "") {
        return explicit;
    }
    if (params.column.columnType === "OttopilotEvent") {
        return params.eventColumnDisplayNameById.get(params.column.columnId) ?? params.column.columnId;
    }
    return params.journeyColumnDisplayNameById.get(params.column.columnId) ?? params.column.columnId;
}

function createFormValueSetter(columnId: Uuid, setFormValues: Dispatch<SetStateAction<Record<string, unknown>>>): Dispatch<SetStateAction<unknown>> {
    return (next) => {
        setFormValues((prev) => {
            const current = prev[columnId];
            const resolved = typeof next === "function" ? (next as (prevState: unknown) => unknown)(current) : next;
            return {
                ...prev,
                [columnId]: resolved,
            };
        });
    };
}

function shouldUseEventSchemaEditComponent(params: {
    schemaColumn: UdoColumn | undefined;
    presentation: OttopilotAdditionalButtonFormFieldPresentation;
}): boolean {
    if (params.schemaColumn === undefined) {
        return false;
    }
    if (params.presentation === OttopilotAdditionalButtonFormFieldPresentation.multiLineText && params.schemaColumn.type === UdoColumnType.string) {
        return false;
    }
    return true;
}

function shouldUseJourneySchemaEditComponent(params: {
    schemaColumn: UdoColumn | undefined;
    presentation: OttopilotAdditionalButtonFormFieldPresentation;
}): boolean {
    if (params.schemaColumn === undefined) {
        return false;
    }
    if (params.schemaColumn.type === UdoColumnType.uuid) {
        return false;
    }
    if (params.presentation === OttopilotAdditionalButtonFormFieldPresentation.multiLineText && params.schemaColumn.type === UdoColumnType.string) {
        return false;
    }
    return true;
}

function renderEventAdditionalColumnEditControl(params: {
    schemaColumn: UdoColumn;
    value: unknown;
    setValue: Dispatch<SetStateAction<unknown>>;
    userDefinedObjectOptionsByTemplateId: ReturnType<typeof useWorkspace>["userDefinedObjectOptionsByTemplateId"];
}): ReactElement {
    const {schemaColumn, value, setValue} = params;
    const details = getUserDefinedObjectAdditionalColumnDetails(schemaColumn);
    const edit = details.editComponent as LooseAdditionalColumnEditComponent;
    if (
        schemaColumn.type === UdoColumnType.userDefinedObject ||
        schemaColumn.type === UdoColumnType.userDefinedObjects
    ) {
        return edit({
            value: value === undefined || value === null
                ? schemaColumn.type === UdoColumnType.userDefinedObjects ? [] : null
                : value,
            setValue: setValue,
            options: getAdditionalColumnOptions(schemaColumn, params.userDefinedObjectOptionsByTemplateId),
        });
    }
    if (
        schemaColumn.type === UdoColumnType.user ||
        schemaColumn.type === UdoColumnType.product
    ) {
        return edit({
            value: value === undefined ? null : value,
            setValue: setValue,
        });
    }
    if (schemaColumn.type === UdoColumnType.ou) {
        return edit({
            value: value === undefined || value === null ? ("" as unknown as Uuid) : value,
            setValue: setValue,
        });
    }
    if (schemaColumn.type === UdoColumnType.userTemplate) {
        return edit({
            value: value === undefined || value === null ? ("" as unknown as Uuid) : value,
            setValue: setValue,
        });
    }
    if (schemaColumn.type === UdoColumnType.timestamp) {
        return edit({
            value: value as Iso8601DateTime,
            setValue: setValue,
        });
    }
    return edit({
        value: unknownToSingleLineFieldString(value),
        setValue: setValue,
    });
}

function renderJourneyAdditionalColumnEditControl(params: {
    schemaColumn: UdoColumn;
    value: unknown;
    setValue: Dispatch<SetStateAction<unknown>>;
    userDefinedObjectOptionsByTemplateId: ReturnType<typeof useWorkspace>["userDefinedObjectOptionsByTemplateId"];
}): ReactElement {
    const {schemaColumn, value, setValue} = params;
    const details = getUserDefinedObjectAdditionalColumnDetails(schemaColumn);
    const edit = details.editComponent as LooseAdditionalColumnEditComponent;
    if (
        schemaColumn.type === UdoColumnType.userDefinedObject ||
        schemaColumn.type === UdoColumnType.userDefinedObjects
    ) {
        return edit({
            value: value === undefined || value === null
                ? schemaColumn.type === UdoColumnType.userDefinedObjects ? [] : null
                : value,
            setValue: setValue,
            options: getAdditionalColumnOptions(schemaColumn, params.userDefinedObjectOptionsByTemplateId),
        });
    }
    if (
        schemaColumn.type === UdoColumnType.user ||
        schemaColumn.type === UdoColumnType.product
    ) {
        return edit({
            value: value === undefined ? null : value,
            setValue: setValue,
        });
    }
    if (schemaColumn.type === UdoColumnType.ou) {
        return edit({
            value: value === undefined || value === null ? ("" as unknown as Uuid) : value,
            setValue: setValue,
        });
    }
    if (schemaColumn.type === UdoColumnType.userTemplate) {
        return edit({
            value: value === undefined || value === null ? ("" as unknown as Uuid) : value,
            setValue: setValue,
        });
    }
    if (schemaColumn.type === UdoColumnType.timestamp) {
        return edit({
            value: value as Iso8601DateTime,
            setValue: setValue,
        });
    }
    return edit({
        value: unknownToSingleLineFieldString(value),
        setValue: setValue,
    });
}

export function EventAdditionalButtonFormModal(props: {
    config: OttopilotAdditionalButtonsConfig;
    event: EventWithEventContainer;
    eventTemplate: OttopilotEventTemplate;
    journeysModuleConfiguration: JourneysModuleConfiguration;
    opened: boolean;
    onClose: () => void;
    onSuccess?: () => void;
    queryKeyPrefix: Array<unknown>;
    workspaceUserTemplateId: Uuid;
    /** When true, primary action stays disabled until required fields (incl. cancel remarks) are filled. */
    submitDisabledUntilValid?: boolean;
}) {
    const {fieldsRef, validateFields} = useUserDefinedObjectFieldValidation();
    const getAdditionalColumns = useCapabilityUdoColumns();
    const {heimdallUser} = useWorkspaceUser();
    const {intellsysBaseUrl} = useOttopilotConfiguration();
    const {workspace, workspaceCapabilities, userDefinedObjectOptionsByTemplateId} = useWorkspace();
    const queryClient = useQueryClient();
    const {showFlag} = useAnankeFlags();

    const sortedColumns = useMemo(() => {
        return getAdditionalButtonFormColumnsSorted(props.config.formColumnData);
    }, [props.config.formColumnData]);

    const visibleEditableColumns = useMemo(() => {
        return sortedColumns.filter((col) => {
            const template = col.columnType === "OttopilotEvent" ? props.eventTemplate : props.journeysModuleConfiguration;
            const column = getAdditionalColumns(template).find(item => item.id === col.columnId);
            return col.isVisible !== false && col.readOnly !== true && column?.source.type !== UdoColumnSource.resolver;
        });
    }, [getAdditionalColumns, sortedColumns, props.eventTemplate, props.journeysModuleConfiguration]);

    const eventColumnDisplayNameById = useMemo(() => {
        return new Map<Uuid, string>(
            getAdditionalColumns(props.eventTemplate).map((column) => {
                return [column.id, column.displayName];
            }),
        );
    }, [getAdditionalColumns, props.eventTemplate]);

    const journeyColumnDisplayNameById = useMemo(() => {
        return new Map<Uuid, string>(
            getAdditionalColumns(props.journeysModuleConfiguration).map((column) => {
                return [column.id, column.displayName];
            }),
        );
    }, [getAdditionalColumns, props.journeysModuleConfiguration]);

    const [formValues, setFormValues] = useState<Record<string, unknown>>({});

    const initialFormValues = useMemo(() => {
        const next: Record<string, unknown> = {};
        for (const col of visibleEditableColumns) {
            next[col.columnId] = buildInitialVisibleFieldValue({
                column: col,
                event: props.event,
            });
        }
        return next;
    }, [visibleEditableColumns, props.event]);

    useEffect(() => {
        if (props.opened === false) {
            return;
        }
        setFormValues(initialFormValues);
    }, [props.opened, props.config.id, props.event.id, initialFormValues]);

    const cancelMutation = useMutation({
        mutationFn: async () => {
            if (!validateFields()) {
                throw new Error("Correct the invalid fields before saving.");
            }
            if (
                isAmaronWorkspace(workspaceCapabilities) === true
                && props.config.buttonKind === OttopilotAdditionalButtonKind.cancel
            ) {
                const remarksCol = getAdditionalButtonCancelRemarksColumn(props.config.formColumnData);
                const cancellationReasonRaw =
                    remarksCol !== undefined ? formValues[remarksCol.columnId] : undefined;
                const cancellationReason =
                    typeof cancellationReasonRaw === "string" ? cancellationReasonRaw.trim() : "";
                if (cancellationReason === "") {
                    throw new Error("Cancellation reason is required.");
                }
                const result = await amaronCancelCallAction({
                    accessToken: heimdallUser.accessToken,
                    intellsysBaseUrl: intellsysBaseUrl,
                    request: {
                        workspaceId: workspace.id,
                        eventId: props.event.id,
                        cancellationReason: cancellationReason,
                    },
                });
                if (result.success !== true) {
                    throw result.error;
                }
                return;
            }

            if (
                isDuluxWorkspace(workspaceCapabilities) === true
                && props.config.buttonKind === OttopilotAdditionalButtonKind.cancel
            ) {
                const remarksCol = getAdditionalButtonCancelRemarksColumn(props.config.formColumnData);
                const cancellationRemarksRaw =
                    remarksCol !== undefined ? formValues[remarksCol.columnId] : undefined;
                const cancellationRemarks =
                    typeof cancellationRemarksRaw === "string" ? cancellationRemarksRaw.trim() : "";
                if (cancellationRemarks === "") {
                    throw new Error("Cancellation reason is required.");
                }
                const result = String(props.event.templateId) === String(DuluxEventTypes.Appointments)
                    ? await duluxCancelAppointmentAction({
                        accessToken: heimdallUser.accessToken,
                        intellsysBaseUrl: intellsysBaseUrl,
                        request: {
                            workspaceId: workspace.id,
                            eventId: props.event.id,
                            cancellationRemarks: cancellationRemarks,
                        },
                    })
                    : await duluxCancelCallAction({
                        accessToken: heimdallUser.accessToken,
                        intellsysBaseUrl: intellsysBaseUrl,
                        request: {
                            workspaceId: workspace.id,
                            eventId: props.event.id,
                            cancellationRemarks: cancellationRemarks,
                        },
                    });
                if (result.success !== true) {
                    throw result.error;
                }
                return;
            }

            const data = buildSubmitPayload({
                sortedColumns: sortedColumns,
                formValues: formValues,
                heimdallUserId: heimdallUser.id,
                workspaceUserTemplateId: props.workspaceUserTemplateId,
            });
            const result = await updateEvent({
                accessToken: heimdallUser.accessToken,
                intellsysBaseUrl: intellsysBaseUrl,
                eventId: props.event.id,
                templateId: props.event.templateId,
                workspaceId: workspace.id,
                data: data as never,
            });
            if (result.success !== true) {
                throw result.error;
            }
        },
        onSuccess: () => {
            showFlag({
                title: "Success",
                description: props.config.description ?? "Updated successfully",
                appearance: "success",
                isAutoDismiss: true,
                autoDismissSeconds: 3,
            });
            props.onClose();
            void invalidateOttopilotDataViewerQueries(queryClient, props.queryKeyPrefix);
            if (props.onSuccess !== undefined) {
                props.onSuccess();
            }
        },
        onError: (error: unknown) => {
            const message = error instanceof Error ? error.message : String(error);
            showFlag({
                title: "Error",
                description: message,
                appearance: "error",
                isAutoDismiss: true,
                autoDismissSeconds: 3,
            });
        },
    });

    const validateForm = (): boolean => {
        for (const col of visibleEditableColumns) {
            if (col.isRequired === true) {
                const v = formValues[col.columnId];
                if (isEmptyForRequiredField(v) === true) {
                    return false;
                }
            }
        }
        if (props.config.buttonKind === OttopilotAdditionalButtonKind.cancel) {
            const remarksCol = getAdditionalButtonCancelRemarksColumn(props.config.formColumnData);
            if (remarksCol !== undefined) {
                const v = formValues[remarksCol.columnId];
                if (isEmptyForRequiredField(v) === true) {
                    return false;
                }
            }
        }
        return true;
    };

    const isFormValid = validateForm();
    const isPrimaryActionDisabled = cancelMutation.isPending === true || (props.submitDisabledUntilValid === true && isFormValid !== true);

    // Guard against losing edits on close / back / refresh while the form is dirty.
    const isDirty =
        props.opened === true
        && Object.keys(formValues).length > 0
        && isFormValueDirty(formValues, initialFormValues);
    const {requestClose, modal} = useUnsavedChangesGuard({isDirty, onDiscard: props.onClose});

    if (props.opened === false) {
        return null;
    }

    return (
        <>
        {modal}
        <GenericModal
            opened={props.opened}
            onClose={() => requestClose()}
            title={props.config.label}
            zIndex={10000}
            size="md"
        >
            <div ref={fieldsRef} className="grid gap-ds-space-200 pt-ds-space-200">
                {props.config.description !== undefined && props.config.description !== null ? (
                    <p className="text-sm font-normal text-ds-text-subtlest">{props.config.description}</p>
                ) : null}
                {visibleEditableColumns.map((col) => {
                    const fieldLabel = getAdditionalButtonFormFieldDisplayLabel({
                        column: col,
                        eventColumnDisplayNameById: eventColumnDisplayNameById,
                        journeyColumnDisplayNameById: journeyColumnDisplayNameById,
                    });
                    const presentation = col.presentation ?? OttopilotAdditionalButtonFormFieldPresentation.singleLineText;
                    const value = formValues[col.columnId];
                    const strValue = unknownToSingleLineFieldString(value);
                    const setValueForCol = createFormValueSetter(col.columnId, setFormValues);

                    const eventSchemaColumn =
                        col.columnType === "OttopilotEvent"
                            ? getAdditionalColumns(props.eventTemplate).find((c) => {
                                  return c.id === col.columnId;
                              })
                            : undefined;
                    const journeySchemaColumn =
                        col.columnType === "OttopilotEventContainer"
                            ? getAdditionalColumns(props.journeysModuleConfiguration).find((c) => {
                                  return c.id === col.columnId;
                              })
                            : undefined;

                    if (
                        col.columnType === "OttopilotEvent" &&
                        shouldUseEventSchemaEditComponent({
                            schemaColumn: eventSchemaColumn,
                            presentation: presentation,
                        }) === true
                    ) {
                        return (
                            <AnankeField
                                key={col.columnId}
                                id={`additional-btn-${props.config.id}-${col.columnId}`}
                                label={fieldLabel}
                                isRequired={col.isRequired === true}
                            >
                                {renderEventAdditionalColumnEditControl({
                                    schemaColumn: eventSchemaColumn!,
                                    value: formValues[col.columnId],
                                    setValue: setValueForCol,
                                    userDefinedObjectOptionsByTemplateId,
                                })}
                            </AnankeField>
                        );
                    }

                    if (
                        col.columnType === "OttopilotEventContainer" &&
                        shouldUseJourneySchemaEditComponent({
                            schemaColumn: journeySchemaColumn,
                            presentation: presentation,
                        }) === true
                    ) {
                        return (
                            <AnankeField
                                key={col.columnId}
                                id={`additional-btn-${props.config.id}-${col.columnId}`}
                                label={fieldLabel}
                                isRequired={col.isRequired === true}
                            >
                                {renderJourneyAdditionalColumnEditControl({
                                    schemaColumn: journeySchemaColumn!,
                                    value: formValues[col.columnId],
                                    setValue: setValueForCol,
                                    userDefinedObjectOptionsByTemplateId,
                                })}
                            </AnankeField>
                        );
                    }

                    if (presentation === OttopilotAdditionalButtonFormFieldPresentation.multiLineText) {
                        return (
                            <AnankeField
                                key={col.columnId}
                                id={`additional-btn-${props.config.id}-${col.columnId}`}
                                label={fieldLabel}
                                isRequired={col.isRequired === true}
                            >
                                <AnankeTextArea
                                    value={strValue}
                                    onChange={(e) => {
                                        const next = e.target instanceof HTMLTextAreaElement ? e.target.value : "";
                                        setFormValues((prev) => {
                                            return {
                                                ...prev,
                                                [col.columnId]: next,
                                            };
                                        });
                                    }}
                                    placeholder={col.placeHolder}
                                    rows={4}
                                />
                            </AnankeField>
                        );
                    }

                    return (
                        <AnankeField
                            key={col.columnId}
                            id={`additional-btn-${props.config.id}-${col.columnId}`}
                            label={fieldLabel}
                            isRequired={col.isRequired === true}
                        >
                            <AnankeTextField
                                value={strValue}
                                placeholder={col.placeHolder}
                                onChange={(e) => {
                                    const next = e.target instanceof HTMLInputElement ? e.target.value : "";
                                    setFormValues((prev) => {
                                        return {
                                            ...prev,
                                            [col.columnId]: next,
                                        };
                                    });
                                }}
                            />
                        </AnankeField>
                    );
                })}

                <div className="grid grid-cols-2 gap-ds-space-150 pt-ds-space-100">
                    <AnankeButton
                        type="button"
                        appearance="subtle"
                        onClick={() => requestClose()}
                    >
                        Cancel
                    </AnankeButton>
                    <AnankeButton
                        type="button"
                        appearance="danger"
                        onClick={() => {
                            if (isFormValid === false) {
                                if (props.submitDisabledUntilValid !== true) {
                                    showFlag({
                                        title: "Validation",
                                        description: "Please fill in all required fields.",
                                        appearance: "warning",
                                        isAutoDismiss: true,
                                        autoDismissSeconds: 3,
                                    });
                                }
                                return;
                            }
                            cancelMutation.mutate();
                        }}
                        isDisabled={isPrimaryActionDisabled}
                        isLoading={cancelMutation.isPending}
                    >
                        {props.config.label}
                    </AnankeButton>
                </div>
            </div>
        </GenericModal>
        </>
    );
}
